import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime } from "@/lib/format";

type GmailConnection = components["schemas"]["GmailConnectionAdminListItem"];
type GmailConnectionStatus = NonNullable<components["schemas"]["GmailConnectionStatus"]>;

interface GmailConnectionsTableProps {
  organisationId?: string;
  clientId?: string;
  emailAddress?: string;
  status?: GmailConnectionStatus;
}

export function statusVariant(
  status: GmailConnectionStatus,
): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Connected":
      return "default";
    case "Backfilling":
      return "secondary";
    case "NeedsReauth":
    case "Revoked":
      return "destructive";
    case "Pending":
    default:
      return "outline";
  }
}

export function GmailConnectionsTable({
  organisationId,
  clientId,
  emailAddress,
  status,
}: GmailConnectionsTableProps) {
  const pagination = useCursorPagination({ initialLimit: 20 });

  const [connections, setConnections] = useState<GmailConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const prevFiltersRef = useRef({ organisationId, clientId, emailAddress, status });
  useEffect(() => {
    if (
      prevFiltersRef.current.organisationId !== organisationId ||
      prevFiltersRef.current.clientId !== clientId ||
      prevFiltersRef.current.emailAddress !== emailAddress ||
      prevFiltersRef.current.status !== status
    ) {
      prevFiltersRef.current = { organisationId, clientId, emailAddress, status };
      pagination.reset();
    }
  }, [organisationId, clientId, emailAddress, status, pagination.reset]);

  useEffect(() => {
    let cancelled = false;

    async function fetchConnections() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/gmail-connections", {
        params: {
          query: {
            OrganisationId: organisationId,
            ClientId: clientId,
            EmailAddress: emailAddress,
            Status: status,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load Gmail connections.");
      } else {
        setConnections(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchConnections();
    return () => {
      cancelled = true;
    };
  }, [
    organisationId,
    clientId,
    emailAddress,
    status,
    pagination.cursor,
    pagination.limit,
  ]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-2 flex h-5 items-center text-sm text-muted-foreground">
        {loading && <Spinner className="size-4" />}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Organisation</TableHead>
              <TableHead>Last synced</TableHead>
              <TableHead>Access token</TableHead>
              <TableHead>Refresh token</TableHead>
              <TableHead>Watch</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {connections.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-muted-foreground h-24 text-center">
                  No Gmail connections found.
                </TableCell>
              </TableRow>
            ) : (
              connections.map((connection) => (
                <TableRow key={connection.id}>
                  <TableCell>
                    <Link to={`/gmail-connections/${connection.id}`}>
                      <Badge
                        variant="secondary"
                        className="font-mono text-xs hover:bg-primary/20"
                      >
                        {connection.id.slice(0, 8)}...
                      </Badge>
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">
                    <Link
                      to={`/gmail-connections/${connection.id}`}
                      className="hover:underline"
                    >
                      {connection.emailAddress}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(connection.status)}>
                      {connection.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Link to={`/organisations/${connection.organisationId}`}>
                      <Badge
                        variant="secondary"
                        className="font-mono text-xs hover:bg-primary/20"
                      >
                        {connection.organisationId.slice(0, 8)}...
                      </Badge>
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">
                    {connection.lastSyncedAt ? (
                      formatDateTime(connection.lastSyncedAt)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={connection.accessTokenExpired ? "destructive" : "default"}>
                      {connection.accessTokenExpired ? "Expired" : "Valid"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={connection.hasRefreshToken ? "default" : "destructive"}>
                      {connection.hasRefreshToken ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={connection.watchExpired ? "destructive" : "default"}>
                      {connection.watchExpired ? "Expired" : "Valid"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDateTime(connection.createdAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
